import { useCallback, useMemo, useState } from "react";

import { useAppSelector } from "@/redux/hooks";

import { useGetCampaignRoutingQuery } from "@/pages/Private/redux/campaignRouting/campaignRouting.api";
import {
	selectCampaignRoutingFailed,
	selectCampaignRoutingSaving,
	selectCampaignRoutingSettings,
} from "@/pages/Private/redux/campaignRouting/campaignRouting.slice";

import {
	RoutingCampaignConfig,
	RoutingCampaignStatuses,
	routingCampaignsOf,
} from "./utils/campaignRoutingTargets";
import { CampaignRoutingEditor } from "./CampaignRoutingEditor";

interface UseCampaignRoutingEditorInput {
	listId: number;
	campaignConfig: RoutingCampaignConfig;
	campaignStatuses: RoutingCampaignStatuses;
	autoAssignEnabled: boolean;
}

let openings = 0;

/** One opening of the editor; `waitsForSave` while a save of the list was in flight at the open. */
interface Opening {
	listId: number;
	key: number;
	waitsForSave: boolean;
}

/**
 * A list's routing (read through the slice) and the editor that changes it. The editor is mounted
 * only while open, for the list it was opened on, and keyed per opening: switching to another list
 * closes it, and every open seeds from the server's state (web §24b). An open while the list's save
 * is still in flight (the editor was closed during it) shows the editor waiting, without rules or
 * Save; when the save settles it is mounted afresh on the settings the save answered.
 */
export const useCampaignRoutingEditor = ({
	listId,
	campaignConfig,
	campaignStatuses,
	autoAssignEnabled,
}: UseCampaignRoutingEditorInput) => {
	useGetCampaignRoutingQuery(listId, { skip: !listId });

	const settings = useAppSelector(selectCampaignRoutingSettings(listId));
	const failed = useAppSelector(selectCampaignRoutingFailed(listId));
	const saving = useAppSelector(selectCampaignRoutingSaving(listId));
	const [opening, setOpening] = useState<Opening | null>(null);

	const campaigns = useMemo(
		() => routingCampaignsOf(campaignConfig, campaignStatuses, settings),
		[campaignConfig, campaignStatuses, settings]
	);

	const open = useCallback(() => {
		openings += 1;
		setOpening({ listId, key: openings, waitsForSave: saving });
	}, [listId, saving]);

	const close = useCallback(() => setOpening(null), []);

	// Another list closes the editor for good: coming back does not reopen it unasked. Adjusted
	// while rendering (React's "state from a prop change"), so no frame shows the old opening.
	if (opening && opening.listId !== listId) {
		setOpening(null);
	}

	// The save it waited for has settled: the next render mounts the editor on the answer.
	if (opening?.waitsForSave && !saving) {
		setOpening({ ...opening, waitsForSave: false });
	}

	const editor =
		opening && opening.listId === listId && settings ? (
			<CampaignRoutingEditor
				autoAssignEnabled={autoAssignEnabled}
				campaigns={campaigns}
				key={`${listId}:${opening.key}:${opening.waitsForSave ? "waiting" : "ready"}`}
				listId={listId}
				settings={settings}
				waitsForSave={opening.waitsForSave}
				onClose={close}
			/>
		) : null;

	return { settings, failed, campaigns, open, editor, canOpen: !!settings };
};
